import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { Plus, X, TrendingUp, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { inrCompact } from '../../lib/format';

const REVISION_REASONS = ['Joining', 'Increment', 'Promotion', 'Reorganization', 'Correction'];

const DEFAULT_COMPONENTS = [
  { code: 'BASIC', name: 'Basic Pay', amount_monthly: 0, is_taxable: true },
  { code: 'HRA', name: 'House Rent Allowance', amount_monthly: 0, is_taxable: true },
  { code: 'CON', name: 'Conveyance', amount_monthly: 0, is_taxable: true },
  { code: 'SA', name: 'Special Allowance', amount_monthly: 0, is_taxable: true },
  { code: 'LTA', name: 'LTA', amount_monthly: 0, is_taxable: false },
  { code: 'MED', name: 'Medical Allowance', amount_monthly: 0, is_taxable: true },
];

const fmt = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

export default function CTCStructure() {
  const [employees, setEmployees] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [ctcData, setCtcData] = useState({ active: null, history: [] });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    effective_from: new Date().toISOString().slice(0, 10),
    revision_reason: 'Increment',
    components: DEFAULT_COMPONENTS.map(c => ({ ...c })),
    employer_pf: 0,
    employer_esi: 0,
    gratuity_monthly: 0,
    other_employer_contributions: 0,
    notes: '',
  });

  useEffect(() => {
    apiGet('/hr/employees').then(list => {
      setEmployees(list);
      if (list.length) setSelectedId(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    apiGet(`/hr/ctc/${selectedId}`).then(setCtcData).catch(() => setCtcData({ active: null, history: [] }));
  }, [selectedId]);

  const gross = form.components.reduce((s, c) => s + (parseFloat(c.amount_monthly) || 0), 0);
  // Ellora policy (Jun-2026): Employer ESI and Gratuity are NOT part of CTC.
  const employerTotal = (parseFloat(form.employer_pf) || 0) + (parseFloat(form.other_employer_contributions) || 0);
  const ctcMonthly = gross + employerTotal;
  const ctcAnnual = ctcMonthly * 12;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await apiPost('/hr/ctc', {
        employee_id: selectedId,
        effective_from: form.effective_from,
        revision_reason: form.revision_reason,
        components: form.components.filter(c => (parseFloat(c.amount_monthly) || 0) > 0),
        employer_pf: parseFloat(form.employer_pf) || 0,
        employer_esi: parseFloat(form.employer_esi) || 0,
        gratuity_monthly: parseFloat(form.gratuity_monthly) || 0,
        other_employer_contributions: parseFloat(form.other_employer_contributions) || 0,
        notes: form.notes,
      });
      toast.success('CTC revision saved');
      setShowForm(false);
      const newCtc = await apiGet(`/hr/ctc/${selectedId}`);
      setCtcData(newCtc);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const updateComp = (idx, value) => {
    const next = [...form.components];
    next[idx] = { ...next[idx], amount_monthly: value };
    setForm({ ...form, components: next });
  };

  const selectedEmp = employees.find(e => e.id === selectedId);

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Payroll</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">CTC Structure</h1>
          <p className="text-sm text-slate-600 mt-1">Per-employee salary structure with revision history. New entry deactivates the previous active one.</p>
        </div>
        {selectedId && (
          <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-ctc-btn">
            <Plus size={16} /> New Revision
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
        <aside className="bg-white border border-slate-200 rounded-sm">
          <div className="px-4 py-3 border-b border-slate-200"><div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500">Employees</div></div>
          <ul className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
            {employees.map(e => (
              <li key={e.id}>
                <button onClick={() => setSelectedId(e.id)} className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${selectedId === e.id ? 'bg-teal-50/60 border-l-2 border-l-[#1F7A6E]' : ''}`}>
                  <div className="font-display font-semibold text-sm text-[#0F172A] truncate">{e.name}</div>
                  <div className="text-xs text-slate-500 truncate">{e.emp_id} · {e.designation || '—'}</div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <main className="space-y-5">
          {selectedEmp && (
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display font-bold text-lg">{selectedEmp.name}</h3>
                  <div className="text-xs text-slate-500">{selectedEmp.emp_id} · {selectedEmp.designation || '—'} · {selectedEmp.department || '—'}</div>
                </div>
                {ctcData.active && (
                  <div className="text-right">
                    <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Current CTC (Annual)</div>
                    <div className="font-display text-3xl font-extrabold text-[#1F7A6E]">{inrCompact(ctcData.active.ctc_annual)}</div>
                    <div className="text-xs text-slate-500">Gross monthly: ₹{ctcData.active.gross_monthly?.toLocaleString('en-IN')}</div>
                  </div>
                )}
              </div>
              {!ctcData.active && (
                <div className="text-center py-8 text-sm text-slate-500">
                  No CTC defined yet. Click <strong>New Revision</strong> to add the joining structure.
                </div>
              )}
              {ctcData.active && (
                <div className="border-t border-slate-100 pt-4">
                  <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 mb-2">Components (Active since {fmt(ctcData.active.effective_from)})</div>
                  <table className="w-full dense-table">
                    <thead><tr><th>Code</th><th>Component</th><th className="text-right">Monthly (₹)</th><th>Taxable</th></tr></thead>
                    <tbody>
                      {(ctcData.active.components || []).map((c, i) => (
                        <tr key={i}>
                          <td className="text-xs font-mono">{c.code}</td>
                          <td className="text-sm">{c.name}</td>
                          <td className="text-right font-display font-bold">{(c.amount_monthly || 0).toLocaleString('en-IN')}</td>
                          <td className="text-xs">{c.is_taxable ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                      {ctcData.active.employer_pf > 0 && <tr className="bg-slate-50"><td className="text-xs font-mono">PF</td><td className="text-sm text-slate-600">Employer PF Contribution</td><td className="text-right font-display font-bold">{ctcData.active.employer_pf.toLocaleString('en-IN')}</td><td className="text-xs text-slate-500">Employer</td></tr>}
                      {ctcData.active.gratuity_monthly > 0 && <tr className="bg-slate-50"><td className="text-xs font-mono">GRAT</td><td className="text-sm text-slate-600">Gratuity</td><td className="text-right font-display font-bold">{ctcData.active.gratuity_monthly.toLocaleString('en-IN')}</td><td className="text-xs text-slate-500">Employer</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {ctcData.history.length > 1 && (
            <div className="bg-white border border-slate-200 rounded-sm">
              <div className="px-5 py-3 border-b border-slate-200"><h3 className="font-display font-bold text-base">Revision History</h3></div>
              <table className="w-full dense-table">
                <thead><tr><th>Effective From</th><th>Reason</th><th className="text-right">Gross/mo</th><th className="text-right">CTC Annual</th><th>Status</th></tr></thead>
                <tbody>
                  {ctcData.history.map(h => (
                    <tr key={h.id}>
                      <td className="text-xs">{fmt(h.effective_from)}</td>
                      <td className="text-xs">{h.revision_reason}</td>
                      <td className="text-right font-display font-bold text-xs">₹{(h.gross_monthly || 0).toLocaleString('en-IN')}</td>
                      <td className="text-right font-display font-bold text-xs">{inrCompact(h.ctc_annual)}</td>
                      <td>{h.is_active ? <span className="tag bg-emerald-100 text-emerald-700"><CheckCircle2 size={11} className="inline mr-1" />Active</span> : <span className="tag bg-slate-100 text-slate-600">Past</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">New CTC Revision</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-500"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Effective From *</label><input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} className="input" required /></div>
                <div>
                  <label className="label">Reason</label>
                  <select value={form.revision_reason} onChange={(e) => setForm({ ...form, revision_reason: e.target.value })} className="input">
                    {REVISION_REASONS.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="label">Earnings (Monthly ₹)</label>
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <table className="w-full dense-table">
                    <thead><tr><th>Code</th><th>Component</th><th className="text-right">Amount</th></tr></thead>
                    <tbody>
                      {form.components.map((c, i) => (
                        <tr key={c.code}>
                          <td className="text-xs font-mono">{c.code}</td>
                          <td className="text-sm">{c.name}</td>
                          <td><input type="number" min="0" value={c.amount_monthly} onChange={(e) => updateComp(i, e.target.value)} className="w-full text-right bg-transparent text-sm focus:outline-none" data-testid={`ctc-${c.code}-input`} /></td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50 font-display font-bold">
                        <td colSpan="2" className="text-sm">Gross Monthly</td>
                        <td className="text-right">₹{gross.toLocaleString('en-IN')}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <label className="label">Employer Contributions (Monthly ₹)</label>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[10px] uppercase tracking-wider text-slate-500">PF (employer)</label><input type="number" value={form.employer_pf} onChange={(e) => setForm({ ...form, employer_pf: e.target.value })} className="input" /></div>
                  <div><label className="text-[10px] uppercase tracking-wider text-slate-500">Other</label><input type="number" value={form.other_employer_contributions} onChange={(e) => setForm({ ...form, other_employer_contributions: e.target.value })} className="input" /></div>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Per Ellora policy, Employer ESI & Gratuity are excluded from CTC. CTC = Gross + Employer PF.</p>
              </div>
              <div className="bg-teal-50 border border-teal-200 rounded-sm p-4 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-display font-bold uppercase tracking-wider text-[#1F7A6E]">CTC Summary</div>
                  <div className="text-xs text-slate-600 mt-1">Monthly ₹{ctcMonthly.toLocaleString('en-IN')} × 12</div>
                </div>
                <div className="font-display text-2xl font-extrabold text-[#1F7A6E]">{inrCompact(ctcAnnual)}</div>
              </div>
              <div><label className="label">Notes</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" rows={2} /></div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="ctc-submit-btn">Save Revision</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
